// src/components/auth/BiometricSetup.jsx
// Component for enrolling biometric authentication (Face ID, Touch ID, fingerprint)

import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { startRegistration } from '@simplewebauthn/browser';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Fingerprint, Loader2, CheckCircle2, AlertTriangle, X } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';

export default function BiometricSetup({ isOpen, onComplete, onSkip, isRequired = false }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [deviceName, setDeviceName] = useState('');

  const handleSetup = async () => {
    setLoading(true);
    setError('');

    try {
      // Step 1: Get registration options from our Edge Function
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData?.session) {
        throw new Error('Not authenticated');
      }

      const optionsResponse = await supabase.functions.invoke('webauthn-register-options', {
        headers: {
          Authorization: `Bearer ${sessionData.session.access_token}`,
        },
      });

      if (optionsResponse.error) {
        logger.error('Registration options invoke error:', optionsResponse.error);
        throw new Error(optionsResponse.error.message || 'Failed to invoke registration options');
      }

      const responseData = optionsResponse.data;
      logger.debug('Registration options response:', responseData);

      // Check if the response indicates an error (new format returns success: false)
      if (responseData?.success === false) {
        const debugInfo = responseData.debug?.join(' → ') || '';
        const errorMessage = responseData.error || 'Failed to get registration options';
        logger.error('Registration options error:', { error: errorMessage, debug: responseData.debug });
        throw new Error(debugInfo ? `${errorMessage} [${debugInfo}]` : errorMessage);
      }

      const options = responseData;
      logger.debug('Got registration options:', { challenge: options.challenge?.substring(0, 10) + '...' });

      // Step 2: Start the WebAuthn registration ceremony
      // This will prompt the user for biometric (Face ID, Touch ID, fingerprint)
      let credential;
      try {
        credential = await startRegistration({ optionsJSON: options });
      } catch (regError) {
        if (regError.name === 'NotAllowedError') {
          throw new Error('Biometric authentication was cancelled or not allowed');
        } else if (regError.name === 'InvalidStateError') {
          throw new Error('This device is already registered');
        } else {
          throw regError;
        }
      }

      logger.debug('Registration credential created');

      // Step 3: Verify the credential with our Edge Function
      const verifyResponse = await supabase.functions.invoke('webauthn-register-verify', {
        headers: {
          Authorization: `Bearer ${sessionData.session.access_token}`,
        },
        body: {
          credential,
          deviceName: deviceName || undefined,
        },
      });

      if (verifyResponse.error) {
        throw new Error(verifyResponse.error.message || 'Failed to verify registration');
      }

      logger.debug('Biometric registration successful:', verifyResponse.data);
      setSuccess(true);
      setDeviceName(verifyResponse.data.deviceName || 'This device');
      toast.success('Logowanie biometryczne zostało włączone!');

      // Wait a moment to show success state, then close
      setTimeout(() => {
        onComplete();
      }, 1500);
    } catch (err) {
      logger.error('Biometric setup error:', err);
      setError(err.message || 'Nie udało się skonfigurować logowania biometrycznego');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    // Store that user skipped this prompt (in localStorage for this device)
    try {
      localStorage.setItem('biometric_setup_skipped', Date.now().toString());
    } catch (e) {
      // Ignore localStorage errors
    }
    onSkip();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isRequired && handleSkip()}>
      <DialogContent 
        className="sm:max-w-md max-h-[90vh] overflow-y-auto"
        overlayClassName={isRequired ? "backdrop-blur-md transition-all duration-500 ease-in-out" : undefined}
        onPointerDownOutside={(e) => isRequired && e.preventDefault()}
        onEscapeKeyDown={(e) => isRequired && e.preventDefault()}
        hideCloseButton={isRequired}
      >
        <DialogHeader className="text-center sm:text-center">
          <div className="mx-auto mb-4">
            <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center">
              {success ? (
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              ) : (
                <Fingerprint className="h-8 w-8 text-green-600" />
              )}
            </div>
          </div>
          <DialogTitle className="text-xl">
            {success ? 'Gotowe!' : 'Włącz szybkie logowanie'}
          </DialogTitle>
          <DialogDescription className="text-center pt-2">
            {success ? (
              `Logowanie biometryczne zostało włączone dla urządzenia "${deviceName}".`
            ) : (
              'Użyj Face ID, Touch ID lub odcisku palca, aby szybko logować się do aplikacji na tym urządzeniu.'
            )}
          </DialogDescription>
        </DialogHeader>

        {!success && (
          <div className="space-y-4 py-4">
            {/* Benefits */}
            <div className="bg-slate-50 rounded-lg p-4 space-y-3">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-slate-700">
                  Loguj się jednym dotknięciem - bez wpisywania hasła
                </p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-slate-700">
                  Twój odcisk palca / Face ID nigdy nie opuszcza urządzenia
                </p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-slate-700">
                  Logowanie hasłem pozostaje dostępne jako alternatywa
                </p>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
          </div>
        )}

        {!success && (
          <DialogFooter className="flex-col sm:flex-col gap-2">
            <Button
              onClick={handleSetup}
              disabled={loading}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Konfigurowanie...
                </>
              ) : (
                <>
                  <Fingerprint className="h-4 w-4 mr-2" />
                  Włącz logowanie biometryczne
                </>
              )}
            </Button>
            <Button
              variant="ghost"
              onClick={handleSkip}
              disabled={loading}
              className="w-full text-slate-500"
            >
              Pomiń na razie
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Helper to check if biometric is available on this device
export async function isBiometricAvailable() {
  try {
    // Check if WebAuthn is supported
    if (!window.PublicKeyCredential) {
      return false;
    }

    // Check if a platform authenticator is available (Face ID, Touch ID, fingerprint)
    const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    return available;
  } catch (e) {
    return false;
  }
}

// Helper to check if we should prompt for biometric setup
export function shouldPromptBiometricSetup() {
  try {
    // Check if user has skipped recently (within 7 days)
    const skippedAt = localStorage.getItem('biometric_setup_skipped');
    if (skippedAt) {
      const daysSinceSkip = (Date.now() - parseInt(skippedAt, 10)) / (1000 * 60 * 60 * 24);
      if (daysSinceSkip < 7) {
        return false;
      }
    }

    // Check if running as PWA or on mobile
    const isPWA = window.matchMedia('(display-mode: standalone)').matches;
    const isMobile = /iPhone|iPad|Android/i.test(navigator.userAgent);

    return isPWA || isMobile;
  } catch (e) {
    return false;
  }
}
